/**
 * Removes inline citation annotations that Azure Foundry / OpenAI agents embed directly
 * in generated answer text (e.g. the full-width bracketed markers around file-search hits).
 * These are internal retrieval markers and should never be shown to end users — sources are
 * surfaced separately.
 *
 * The server strips these from the final answer, but during streaming the raw chunks still
 * contain them, so we also strip here to avoid the markers flashing on screen mid-stream.
 */

// A full-width bracketed annotation containing a dagger, plus any trailing space/tab, e.g.
// 【4:2†source】. Requiring the dagger leaves ordinary CJK bracket usage intact; consuming
// only trailing horizontal whitespace avoids touching legitimate spacing elsewhere.
// The first class excludes the dagger ([^】†]) so the two quantifiers can't both match it —
// this prevents catastrophic backtracking on malformed input (the browser has no regex
// timeout, and this runs on the whole growing answer on every stream chunk).
const CITATION_MARKER = /【[^】†]*†[^】]*】[ \t]*/g;

// A dangling, not-yet-closed marker at the very end of a partial stream, e.g. `【4:2†sou`.
// Stripping it prevents a half-arrived marker from flashing before its closing bracket lands.
// The dagger is required so this only hides a genuine citation-in-progress; a lone `【` in
// legitimate text is left alone, keeping the live view consistent with the final server answer.
const TRAILING_PARTIAL_MARKER = /【[^】†]*†[^】]*$/;

export function stripInlineCitations(text: string): string {
    if (!text || text.indexOf('【') === -1) return text;

    return text
        .replace(CITATION_MARKER, '')
        .replace(TRAILING_PARTIAL_MARKER, '');
}
